import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Modal, Progress, Button, message as $message } from 'antd'
import ReactDOM from 'react-dom'
import { getStore } from '@/utils/dva'
import { DOING, COMPLETE, END } from '@/modules/globals/constants/progress'
import { poll } from '@/utils/utils'
import { createAPI } from '@/utils/modal'
import { queryTaskFn, getExtraParams } from '@/modules/globals/components/MessageCenter/helper'
import { MANUALLY_PULL_ORDERS, BATCH_SHARE_GOODS } from '@/modules/globals/constants/messageCenter'
import styles from './index.less'

const contentMap = {
    [DOING]: ({ progressHeader, progressFooter, dataSource }) => {
        const curProcess = dataSource?.curProcess
        return (
            <div className={styles.doingContainer}>
                {typeof progressHeader === 'function' ? (
                    progressHeader({ dataSource })
                ) : (
                    <div className={styles.progressHeader}>{progressHeader}</div>
                )}
                <Progress
                    strokeColor={{
                        '0%': '#1890FF',
                        '100%': '#1890FF'
                    }}
                    percent={curProcess}
                />
                {typeof progressFooter === 'function' ? (
                    progressFooter({ dataSource })
                ) : (
                    <div className={styles.progressFooter}>{progressFooter}</div>
                )}
            </div>
        )
    },
    [COMPLETE]: ({ completeContent, onCancel, dataSource }) => {
        return (
            <div className={styles.completeContainer}>
                {typeof completeContent === 'function' ? completeContent({ dataSource, onCancel }) : completeContent}
            </div>
        )
    }
}

const defaultFooterBtn = ({ onCancel }) => {
    return <Button onClick={onCancel}>跳过等待</Button>
}

export const ProgressModal = ({
    visible,
    taskId,
    onComplete,
    messageType,
    renderContentMap,
    onCancel: modalCancel,
    onError,
    durationTime = 1000,
    keepTaskWhenClose,
    ...restProps
}) => {
    const $this = useRef({
        progressPollClear: null,
        doing: true
    })

    const [state, setState] = useState(DOING)
    const [dataSource, setDataSource] = useState({})

    useEffect(() => {
        $this.current.progressPollClear = poll(
            async next => {
                const { success, message, data, errorMsg } = await queryTaskFn(messageType)({
                    taskId,
                    ...getExtraParams(messageType)
                }) // data taskState wait: 等待中  process: 处理中  end:任务结束
                if (!success) {
                    $message.error({
                        key: taskId,
                        content: message || '进度条出错了'
                    })
                    modalCancel?.()

                    // 手工拉去订单有很多意外的情况
                    if (messageType === MANUALLY_PULL_ORDERS) {
                        $this.current.doing = false
                        ReactDOM.unstable_batchedUpdates(() => {
                            setState(COMPLETE)
                            setDataSource(data)
                        })
                        onError && onError({ cancel: modalCancel, res: { success, message, data } })
                    }

                    return
                }

                const taskState = data?.taskState

                if (taskState === END) {
                    // 导入分享商品异常情况需要关闭提示弹窗
                    if (messageType === BATCH_SHARE_GOODS && errorMsg) {
                        $this.current.doing = false
                        modalCancel()
                        $message.error({
                            key: taskId,
                            content: errorMsg || '进度条出错了'
                        })
                        return
                    }
                    $this.current.doing = false
                    ReactDOM.unstable_batchedUpdates(() => {
                        setState(COMPLETE)
                        setDataSource(data)
                    })

                    onComplete && onComplete({ cancel: modalCancel, dataSource: data })
                } else {
                    setDataSource(data)
                    next()
                }
            },
            { immediately: true, duration: durationTime }
        )

        return () => {
            if (!keepTaskWhenClose) {
                $this.current.progressPollClear && $this.current.progressPollClear()
            }
        }
    }, [taskId, keepTaskWhenClose])

    const { title, footerBtn, closable, ...renderFn } = useMemo(() => {
        let renderContentMapNew = renderContentMap
        if (typeof renderContentMap === 'function') {
            renderContentMapNew = renderContentMap(dataSource)
        }
        return {
            footerBtn: defaultFooterBtn,
            ...renderContentMapNew[state],
            closable: renderContentMapNew[state]?.closable === false ? false : true
        }
    }, [renderContentMap, state, dataSource])

    const Content = useMemo(() => {
        return contentMap[state]
    }, [state])

    const onCancel = () => {
        if (!keepTaskWhenClose) {
            modalCancel({
                doing: $this.current.doing
            })
        } else {
            // 没有小窗口
            modalCancel()
        }
    }

    return (
        <Modal
            title={title}
            width="551px"
            closable={closable}
            visible={visible}
            footer={footerBtn({ onCancel, dataSource, taskId })}
            maskClosable={false}
            destroyOnClose={true}
            onCancel={onCancel}
            {...restProps}
        >
            <Content {...renderFn} dataSource={dataSource} onCancel={onCancel} />
        </Modal>
    )
}

/**
 * 触发进度条弹窗（支持刷新页面再显示）
 * @param {object} props - React props。
 * @param {string} props.taskId - 任务ID
 * @param {string} props.messageType - 消息类型，需要定义到 @/modules/globals/constants/messageCenter 中
 * @param {object} props.renderContentMap - 根据任务状态渲染不同内容的组件映射。
 * @param {function} props.onCancel - 取消按钮点击事件处理函数。
 * @param {function} props.onComplete - 任务完成时的回调函数。
 * @param {function} props.onError - 任务出错时的回调函数。
 * @param {function} props.durationTime - 任务轮询时间间隔。
 * @returns {function} returns.close - 用于关闭进度提示弹窗的函数。
 */
export const progressPrompt = ({
    taskId,
    onToSee,
    renderContentMap = {
        [DOING]: { title: '', progressHeader: null, progressFooter: null, closable: true },
        [COMPLETE]: { title: '', completeContent: null, footerBtn: null, closable: true }
    },
    messageType = '',
    onCancel,
    onComplete,
    durationTime,
    keepTaskWhenClose = false, // 关闭时是否清除任务
    onError
}) => {
    const { close } = createAPI(ProgressModal, {
        taskId,
        renderContentMap,
        onComplete,
        onError,
        durationTime,
        messageType,
        keepTaskWhenClose,
        onCancel: props => {
            const doing = props?.doing
            if (doing) {
                const $store = getStore()
                const currentUser = $store.getState()?.user?.currentUser

                $store.dispatch({
                    type: 'progress/add',
                    payload: {
                        taskId,
                        onToSee,
                        messageType,
                        currentUser
                    }
                })
            }

            close()

            onCancel &&
                onCancel({
                    doing
                })
        }
    })
    return {
        close
    }
}
